import { type MouseEvent, useRef } from "react";
import { useTranslations } from "use-intl";

import type { CardInfo } from "../billing.types";

const MAX_TILT = 10; // degrees at the card edges

// The card tilts toward the cursor in 3D and a glare follows it.
export function PaymentCard({ card }: { card: CardInfo }) {
	const t = useTranslations("billing.paymentCard");
	const exp = `${String(card.expMonth).padStart(2, "0")}/${String(card.expYear).slice(-2)}`;
	const ref = useRef<HTMLDivElement>(null);

	// The glare layer's radial gradient reads --mx.
	function track(e: MouseEvent<HTMLDivElement>) {
		const el = ref.current;
		if (!el) return;
		const r = el.getBoundingClientRect();
		const fx = (e.clientX - r.left) / r.width;
		const fy = (e.clientY - r.top) / r.height;
		// The slow glide is only for the reset.
		el.style.transitionDuration = "150ms";
		el.style.setProperty("--mx", `${fx * 100}%`);
		el.style.transform = `rotateY(${(fx - 0.5) * MAX_TILT * 2}deg) rotateX(${-(fy - 0.5) * MAX_TILT * 2}deg)`;
	}
	function reset() {
		const el = ref.current;
		if (!el) return;
		el.style.transitionDuration = "1200ms";
		el.style.transform = "";
		el.style.setProperty("--mx", "50%");
	}

	return (
		<div className="group w-full max-w-sm [perspective:1200px]">
			<div
				ref={ref}
				role="img"
				onMouseMove={track}
				onMouseLeave={reset}
				className="relative aspect-[1.586/1] transform-gpu overflow-hidden rounded-2xl bg-neutral-950 p-6 text-white shadow-xl ring-1 ring-white/10 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:shadow-2xl"
			>
				<div
					className="pointer-events-none absolute inset-0 opacity-0 mix-blend-overlay transition-[background-position,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-100"
					style={{
						background:
							"linear-gradient(105deg, transparent 33%, rgba(255,255,255,0.8) 50%, transparent 67%)",
						backgroundSize: "250% 100%",
						backgroundPositionX: "var(--mx, 50%)",
					}}
				/>

				<div className="relative flex h-full flex-col justify-between">
					<div className="flex justify-end">
						<span className="text-sm font-semibold tracking-widest uppercase">{card.brand}</span>
					</div>

					<div className="font-mono text-xl tracking-[0.25em]">•••• •••• •••• {card.last4}</div>

					<div>
						<div className="text-[10px] tracking-wider text-white/40 uppercase">{t("expires")}</div>
						<div className="font-mono text-sm">{exp}</div>
					</div>
				</div>
			</div>
		</div>
	);
}
